/**
 * CSRF Protection Utility
 * Generates and validates CSRF tokens for form submissions
 */

/**
 * Generate a CSRF token
 * @returns {string} CSRF token
 */
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Store CSRF token in sessionStorage
 * @param {string} token - CSRF token to store
 */
export const storeCSRFToken = (token) => {
  try {
    sessionStorage.setItem('csrf_token', token);
  } catch (err) {
    console.error('Failed to store CSRF token:', err);
  }
};

/**
 * Get CSRF token from sessionStorage
 * @returns {string|null} CSRF token or null
 */
export const getCSRFToken = () => {
  try {
    return sessionStorage.getItem('csrf_token');
  } catch (err) {
    console.error('Failed to get CSRF token:', err);
    return null;
  }
};

/**
 * Validate CSRF token
 * @param {string} token - Token to validate
 * @returns {boolean} True if token is valid
 */
export const validateCSRFToken = (token) => {
  const storedToken = getCSRFToken();
  if (!storedToken || !token) return false;
  return storedToken === token;
};

/**
 * Initialize CSRF protection (call on app load)
 * @returns {string} Generated CSRF token
 */
export const initCSRFProtection = () => {
  let token = getCSRFToken();
  if (!token) {
    token = generateCSRFToken();
    storeCSRFToken(token);
  }
  return token;
};

/**
 * Get CSRF token for form submission
 * Creates new token if none exists
 * @returns {string} CSRF token
 */
export const getOrCreateCSRFToken = () => {
  let token = getCSRFToken();
  if (!token) {
    token = generateCSRFToken();
    storeCSRFToken(token);
  }
  return token;
};

/**
 * Add CSRF token to form data
 * @param {object} formData - Form data object
 * @returns {object} Form data with CSRF token
 */
export const addCSRFToken = (formData) => {
  const token = getOrCreateCSRFToken();
  return {
    ...formData,
    _csrf: token,
  };
};

/**
 * Validate CSRF token in form data
 * @param {object} formData - Form data with CSRF token
 * @returns {boolean} True if token is valid
 */
export const validateFormCSRF = (formData) => {
  if (!formData || !formData._csrf) {
    return false;
  }
  return validateCSRFToken(formData._csrf);
};

